import { Observable, type ReadonlyObservable } from "@/lib/studio/observable";
import {
  MediaTiming,
  audioContent,
  type MediaContent,
  type MediaPlayout,
  type MediaTimingChange,
} from "../media-timing";
import type {
  AudioCandidate,
  AudioOutput,
  AudioProfile,
  AudioSource,
  AudioTransport,
  LocalMicrophone,
} from "./audio-types";
import { PublicationLostError } from "../publication-lost";
import { openMicrophoneTrack } from "./microphone-capture";
import { WebAudioOutput } from "./web-audio-output";

export interface AudioEngineOptions {
  transport: AudioTransport;
  /** Où faire entendre les sons reçus. Par défaut, WebAudio, dans un contexte ouvert au premier son. */
  output?: AudioOutput;
  /**
   * Le calage de la séance, partagé avec le moteur vidéo : une voix s'y cale sur
   * le visage de son compte quand la parole passe au plus tôt. Sans lui, le
   * moteur a le sien.
   */
  timing?: MediaTiming;
  /** Ouvre un micro. Injecté par les tests. */
  openMicrophone?: (deviceId: string | null, profile: AudioProfile) => Promise<MediaStreamTrack>;
  /** Tous les combien le retard des sons joués suit ce qu'ils prennent en route, en ms. */
  cadenceMs?: number;
}

/**
 * Tous les combien, en ms, le retard d'un son joué suit ce qu'il a pris en
 * route : le tampon de réception suit la gigue du réseau à l'échelle de la
 * seconde, et la mesure se lisse sur deux.
 */
export const PLAYOUT_CADENCE_MS = 250;

/**
 * Le trajet qu'on prête à un son tant qu'il n'est pas mesuré, en ms : quelques
 * dizaines de millisecondes de réseau, autant de tampon de réception. Prêter
 * davantage ferait sauter le retard de plusieurs centaines de millisecondes au
 * premier relevé.
 */
export const UNMEASURED_LAG_MS = 80;

/** Levée par un geste que la fermeture du moteur, ou la fin de la séance, a interrompu. */
export class AudioEngineClosedError extends Error {
  constructor() {
    super("Moteur audio fermé.");
    this.name = "AudioEngineClosedError";
  }
}

/** Ce que le moteur applique à une source jouée. */
export interface AudioPlayoutDiagnostic {
  id: string;
  /** Ce qu'il porte : du jeu, ou de la parole. */
  content: MediaContent;
  /** La retenue voulue, derrière la prise du son. */
  holdMs: number;
  /** Ce que le son a pris en route, mesuré ; `null` sans mesure. */
  lagMs: number | null;
  /** Ce que la sortie ajoute avant l'oreille. */
  outputLatencyMs: number;
  /** Le retard posé après le décodage. */
  delayMs: number;
}

/** Une voix suivie par le calage, et de quoi cesser de la suivre. */
interface Followed {
  owner: string;
  unfollow: () => void;
}

/**
 * Le moteur audio d'une séance : publier son micro, entendre ceux de la salle,
 * et caler chaque son reçu — un instrument toujours sur les notes, une voix
 * comme la séance le veut (voir `lib/live/media-timing.ts`).
 *
 * Il ne décide ni qui parle, ni qui on entend, ni si une séance porte du son :
 * ce sont les règles du contexte, qui s'appuient sur cette API. Aucun n'en
 * porte aujourd'hui — le récital n'a pas de son —, le cours sera le premier. Il
 * ne connaît ni LiveKit ni React. Voir `docs/spec/audio-engine.md`.
 */
export class AudioEngine {
  private readonly transport: AudioTransport;
  private readonly output: AudioOutput;
  private readonly timing: MediaTiming;
  private readonly ownsTiming: boolean;
  private readonly openMicrophone: (deviceId: string | null, profile: AudioProfile) => Promise<MediaStreamTrack>;
  private readonly cadenceMs: number;
  private readonly detach: () => void;
  private readonly stopTiming: () => void;

  private readonly _sources = new Observable<readonly AudioSource[]>([]);
  private readonly _microphone = new Observable<LocalMicrophone | null>(null);
  private readonly _listening = new Observable<boolean>(false);

  /** Le volume demandé par source présente ; les autres jouent à 1. */
  private readonly volumes = new Map<string, number>();
  /**
   * Ce qui joue, par source, avec le flux joué : une source dont le flux change
   * — une piste reçue remplacée — se rejoue sur le neuf.
   */
  private readonly playing = new Map<string, MediaStream>();
  /** Les voix jouées que le calage suit, par source. */
  private readonly followed = new Map<string, Followed>();
  private microphoneTrack: MediaStreamTrack | null = null;
  /** Le dernier geste en cours : le suivant l'attend. Voir `serialize`. */
  private gestures: Promise<unknown> = Promise.resolve();
  private cadence: ReturnType<typeof setInterval> | null = null;
  /** Change à chaque `release` : un geste commencé pour une séance ne finit pas dans la suivante. */
  private generation = 0;
  private closed = false;

  constructor(options: AudioEngineOptions) {
    this.transport = options.transport;
    this.output = options.output ?? new WebAudioOutput();
    this.timing = options.timing ?? new MediaTiming();
    this.ownsTiming = options.timing === undefined;
    this.openMicrophone = options.openMicrophone ?? openMicrophoneTrack;
    this.cadenceMs = options.cadenceMs ?? PLAYOUT_CADENCE_MS;
    this.detach = this.transport.attach({
      sources: (next) => this.receive(next),
      wants: (candidate) => this.wants(candidate),
      disconnected: () => this.release(),
    });
    this.stopTiming = this.timing.onChange((change) => this.follow(change));

    // Un calage partagé peut déjà tenir l'horloge : ce qui part se date avec elle
    // dès maintenant.
    if (this.timing.playout.clockOffsetMs !== null) {
      this.transport.setClock(this.timing.playout.clockOffsetMs);
    }
  }

  /** Les sources de la salle, celle de ce poste comprise, de la plus ancienne à la plus récente. */
  get sources(): ReadonlyObservable<readonly AudioSource[]> {
    return this._sources;
  }

  /** Le micro que ce poste publie, ou `null`. */
  get microphone(): ReadonlyObservable<LocalMicrophone | null> {
    return this._microphone;
  }

  /** Vrai quand ce poste entend la salle. Voir `setListening`. */
  get listening(): ReadonlyObservable<boolean> {
    return this._listening;
  }

  /** Vrai tant qu'un son devrait jouer et que le navigateur le retient faute d'un geste : `resume` le relance. */
  get blocked(): ReadonlyObservable<boolean> {
    return this.output.blocked;
  }

  /** La référence de propriétaire de ce poste, une fois connecté. */
  get self(): string | null {
    return this.transport.localOwner();
  }

  /**
   * Ouvre le micro de ce poste et le publie, à la place du précédent.
   *
   * `profile` dit ce qu'il prend, donc comment l'ouvrir et sur quoi les autres
   * le calent : la voix par défaut, que la séance peut faire passer au plus
   * tôt ; un instrument, toujours avec les notes.
   *
   * La piste appartient au moteur : il l'arrête à `stopMicrophone`, à la
   * fermeture, et quand elle se termine d'elle-même. Un micro resté ouvert après
   * « Couper » serait la pire façon d'apprendre qu'il écoutait encore. Changer de
   * micro garde la sourdine : choisir un autre périphérique ne fait pas parler
   * dans la salle. Le refus du navigateur remonte tel quel.
   */
  startMicrophone(options: { deviceId?: string | null; profile?: AudioProfile } = {}): Promise<LocalMicrophone> {
    return this.serialize(() => this.openAndPublish(options.deviceId ?? null, options.profile ?? "voice"));
  }

  /** Retire le micro de la salle et l'éteint. Sans effet s'il n'y en a pas. */
  stopMicrophone(): Promise<void> {
    return this.serialize(() => this.unpublishMicrophone());
  }

  /** Met le micro en sourdine, ou l'en sort, sans le retirer de la salle. Sans effet sans micro. */
  setMicrophoneMuted(muted: boolean): Promise<void> {
    return this.serialize(() => this.applyMuted(muted));
  }

  /**
   * Fait entendre la salle à ce poste, ou la fait taire.
   *
   * Faux par défaut : c'est le contexte qui décide s'il porte du son, et un
   * récital n'en porte pas. Tant qu'il est faux, le transport ne s'abonne à
   * aucun son : un micro publié par un client modifié n'atteint personne. À
   * l'écoute, le moteur fait entendre toutes les sources que ce poste ne possède
   * pas.
   */
  setListening(listening: boolean): void {
    if (this.closed || this._listening.value === listening) {
      return;
    }

    this._listening.set(listening);
    this.transport.refresh();
    this.syncPlayback();
  }

  /** Le volume d'une source présente, de 0 à 1. */
  setVolume(sourceId: string, volume: number): void {
    const clamped = Math.min(1, Math.max(0, volume));

    this.volumes.set(sourceId, clamped);

    if (this.playing.has(sourceId)) {
      this.output.setVolume(sourceId, clamped);
    }
  }

  /** Dans un geste de l'utilisateur : relance le son que le navigateur retenait. */
  resume(): Promise<void> {
    return this.output.resume();
  }

  /**
   * L'horloge de séance et le délai que ce poste applique aux notes des autres,
   * posés sur le calage de la séance : un instrument est retardé de ce délai —
   * une voix aussi, sauf si la séance la fait passer au plus tôt —, et rien de
   * ce poste ne s'entend.
   */
  setPlayout(playout: MediaPlayout): void {
    this.timing.setPlayout(playout);
  }

  /** Ce que le moteur applique en ce moment à chaque source jouée, pour la sonde de débogage. */
  diagnostics(): AudioPlayoutDiagnostic[] {
    return this._sources.value
      .filter((source) => this.playing.has(source.id))
      .map((source) => this.playoutOf(source));
  }

  /**
   * Lâche ce que la séance tenait — le micro, les sources, le son qui joue —
   * sans fermer le moteur. À la déconnexion, voulue ou subie : rien ne doit
   * rester ouvert pour une salle partie. L'écoute reste ce que le contexte a
   * choisi.
   */
  release(): void {
    this.generation += 1;
    this.dropMicrophone();
    this._sources.set([]);
    this.volumes.clear();
    this.syncPlayback();
  }

  dispose(): void {
    if (this.closed) {
      return;
    }

    this.closed = true;
    this.release();
    this.detach();
    this.stopTiming();

    if (this.ownsTiming) {
      this.timing.dispose();
    }

    this.output.dispose();
    this._sources.dispose();
    this._microphone.dispose();
    this._listening.dispose();
  }

  private async openAndPublish(deviceId: string | null, profile: AudioProfile): Promise<LocalMicrophone> {
    this.assertOpen();

    const generation = this.generation;
    const muted = this._microphone.value?.muted ?? false;
    const track = await this.openMicrophone(deviceId, profile);

    // Fermé, ou la séance partie, pendant que le navigateur demandait la
    // permission : le micro ouvert n'a plus nulle part où aller.
    if (this.interrupted(generation)) {
      track.stop();

      throw new AudioEngineClosedError();
    }

    // En sourdine avant de partir, et publié éteint : rien ne file.
    track.enabled = !muted;

    try {
      await this.transport.publish(track, profile, muted);
    } catch (error) {
      track.stop();

      // L'ancien micro a quitté la salle sans que le nouveau y entre : rien ne doit
      // rester ouvert ni affiché pour un micro que personne n'entend.
      if (error instanceof PublicationLostError) {
        this.dropMicrophone();
      }

      throw error;
    }

    // Fermé, ou la séance partie, pendant la publication : rien ne doit rester
    // ouvert pour une salle qui n'est plus là.
    if (this.interrupted(generation)) {
      track.stop();

      throw new AudioEngineClosedError();
    }

    const previous = this.microphoneTrack;
    const microphone: LocalMicrophone = {
      stream: new MediaStream([track]),
      deviceId: track.getSettings().deviceId ?? null,
      label: track.label === "" ? "Micro" : track.label,
      profile,
      muted,
    };

    this.microphoneTrack = track;
    this._microphone.set(microphone);

    if (previous !== null && previous !== track) {
      previous.stop();
    }

    // Débranché, ou la permission retirée : le micro ne doit pas rester affiché.
    track.addEventListener("ended", () => {
      if (this.microphoneTrack === track) {
        void this.stopMicrophone();
      }
    });

    return microphone;
  }

  private async unpublishMicrophone(): Promise<void> {
    const track = this.microphoneTrack;

    if (track === null) {
      return;
    }

    this.microphoneTrack = null;
    this._microphone.set(null);
    // Muet tout de suite : sortir de la salle prend un aller-retour, et le micro
    // ne doit pas parler pendant ce temps.
    track.enabled = false;
    await this.transport.unpublish().catch(() => undefined);
    track.stop();
  }

  /** Éteint le micro que tient le moteur, sans rien demander à la salle. */
  private dropMicrophone(): void {
    const track = this.microphoneTrack;

    this.microphoneTrack = null;
    this._microphone.set(null);
    track?.stop();
  }

  private async applyMuted(muted: boolean): Promise<void> {
    const microphone = this._microphone.value;
    const track = this.microphoneTrack;

    if (microphone === null || track === null || microphone.muted === muted) {
      return;
    }

    // La piste d'abord : c'est elle qui fait taire le micro, quoi qu'il advienne
    // du signal. La salle ensuite, pour que les autres voient la sourdine.
    track.enabled = !muted;
    this._microphone.set({ ...microphone, muted });
    await this.transport.setMuted(muted).catch((cause: unknown) => {
      console.warn("Sourdine du micro :", cause);
    });
  }

  /**
   * Un geste à la fois. Deux `startMicrophone` qui se chevauchent publieraient
   * deux pistes, et un `stopMicrophone` pendant une publication laisserait le
   * micro ouvert après « Couper ». Chaque geste attend la fin du précédent,
   * qu'il ait abouti ou non.
   */
  private serialize<T>(gesture: () => Promise<T>): Promise<T> {
    const run = this.gestures.then(gesture, gesture);

    this.gestures = run.catch(() => undefined);

    return run;
  }

  private receive(next: readonly AudioSource[]): void {
    const present = new Set(next.map((source) => source.id));

    for (const id of Array.from(this.volumes.keys())) {
      if (!present.has(id)) {
        this.volumes.delete(id);
      }
    }

    this._sources.set(next);
    this.syncPlayback();
  }

  /**
   * Fait entendre ce qui doit l'être, arrête le reste, et tient la cadence du
   * retard tant que quelque chose joue.
   */
  private syncPlayback(): void {
    const audible = new Map<string, AudioSource>();

    if (this._listening.value && !this.closed) {
      for (const source of this._sources.value) {
        if (!this.ownedHere(source)) {
          audible.set(source.id, source);
        }
      }
    }

    for (const [id, stream] of Array.from(this.playing)) {
      if (audible.get(id)?.stream !== stream) {
        this.output.stop(id);
        this.playing.delete(id);
      }
    }

    // Avant de jouer : une voix qui arrive peut devoir attendre un visage déjà là.
    this.syncFollowed(audible);

    for (const [id, source] of audible) {
      if (!this.playing.has(id)) {
        this.output.play(id, source.stream, this.delayFor(source), this.volumes.get(id) ?? 1);
        this.playing.set(id, source.stream);
      }
    }

    this.syncCadence();
  }

  /**
   * Les voix qu'on entend se font suivre par le calage : quand la parole passe
   * au plus tôt, ce qu'elles mettent à se faire entendre dit ce que le visage de
   * leur compte attend. Une voix qu'on n'entend pas, ou en sourdine, ne fait
   * attendre personne.
   */
  private syncFollowed(audible: ReadonlyMap<string, AudioSource>): void {
    for (const [id, followed] of Array.from(this.followed)) {
      const source = audible.get(id);

      if (source === undefined || source.muted || source.owner !== followed.owner || audioContent(source.profile) !== "speech") {
        this.followed.delete(id);
        followed.unfollow();
      }
    }

    for (const [id, source] of audible) {
      if (audioContent(source.profile) !== "speech" || source.muted || this.followed.has(id)) {
        continue;
      }

      this.followed.set(id, {
        owner: source.owner,
        unfollow: this.timing.follow({ id, owner: source.owner, naturalMs: () => this.naturalOf(id) }),
      });
    }
  }

  /** Suit le calage : l'horloge part au transport, et une retenue qui change retarde autrement ce qui joue. */
  private follow(change: MediaTimingChange): void {
    if (change.clock) {
      this.transport.setClock(this.timing.playout.clockOffsetMs);
    }

    if (change.notes || change.speech) {
      this.retime();
    }
  }

  /** Le retard de chaque son joué suit la retenue et ce qu'il a pris en route. */
  private retime(): void {
    for (const source of this._sources.value) {
      if (this.playing.has(source.id)) {
        this.output.setDelay(source.id, this.delayFor(source));
      }
    }
  }

  private syncCadence(): void {
    if (this.playing.size > 0 && this.cadence === null) {
      this.cadence = setInterval(() => this.retime(), this.cadenceMs);

      return;
    }

    if (this.playing.size === 0 && this.cadence !== null) {
      clearInterval(this.cadence);
      this.cadence = null;
    }
  }

  /** Ce son est-il à ce poste : publié ici, ou par un autre poste du même compte. */
  private ownedHere(source: { owner: string; local?: boolean }): boolean {
    const self = this.self;

    return source.local === true || (self !== null && source.owner === self);
  }

  /** On n'écoute que si le contexte le veut, et jamais soi-même : son propre son reviendrait en écho. */
  private wants(candidate: AudioCandidate): boolean {
    return this._listening.value && !this.closed && !this.ownedHere(candidate);
  }

  /**
   * Ce qui est à soi ne s'entend pas ; les autres, avec le délai de leurs notes —
   * ou, pour une voix quand la parole passe au plus tôt, calés sur leur visage.
   */
  private holdFor(source: AudioSource): number {
    if (this.ownedHere(source)) {
      return 0;
    }

    return this.timing.holdFor(audioContent(source.profile), source.owner, source.id);
  }

  /** Ce qu'une voix met à se faire entendre, sans retard : son trajet mesuré, plus la sortie. `null` sans mesure. */
  private naturalOf(sourceId: string): number | null {
    const lagMs = this.transport.lagOf(sourceId);

    return lagMs === null ? null : lagMs + this.output.latencyMs();
  }

  private delayFor(source: AudioSource): number {
    return this.playoutOf(source).delayMs;
  }

  /**
   * Ce qui se pose sur une source, chaque grandeur lue une seule fois : le retard
   * à poser après le décodage vaut la retenue, moins ce que le son a déjà pris en
   * route, moins ce que la sortie ajoute avant l'oreille. Sans mesure, on prête
   * au son un trajet ordinaire (`UNMEASURED_LAG_MS`).
   */
  private playoutOf(source: AudioSource): AudioPlayoutDiagnostic {
    const holdMs = this.holdFor(source);
    const lagMs = this.transport.lagOf(source.id);
    const outputLatencyMs = this.output.latencyMs();

    return {
      id: source.id,
      content: audioContent(source.profile),
      holdMs,
      lagMs,
      outputLatencyMs,
      delayMs: Math.max(0, holdMs - (lagMs ?? UNMEASURED_LAG_MS) - outputLatencyMs),
    };
  }

  private assertOpen(): void {
    if (this.closed) {
      throw new AudioEngineClosedError();
    }
  }

  /** Le moteur a-t-il été fermé, ou la séance lâchée, depuis le début d'un geste ? */
  private interrupted(generation: number): boolean {
    return this.closed || this.generation !== generation;
  }
}
